import type { QueryClient } from '@tanstack/react-query';
import {
  Link,
  Outlet,
  createRootRouteWithContext,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <>
      <Outlet />
      <Toaster richColors closeButton />
    </>
  ),
  notFoundComponent: () => (
    <CenteredMessage
      title="Page not found"
      message="The page you were looking for doesn't exist."
    />
  ),
  errorComponent: ({ error, reset }: ErrorComponentProps) => (
    <CenteredMessage
      title="Something went wrong"
      message={error instanceof Error ? error.message : String(error)}
    >
      <Button variant="outline" onClick={reset}>
        Try again
      </Button>
    </CenteredMessage>
  ),
});

function CenteredMessage({
  title,
  message,
  children,
}: {
  title: string;
  message: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="mx-auto max-w-md px-4 py-24 text-center">
      <h1 className="text-2xl font-semibold">{title}</h1>
      <p className="text-muted-foreground mt-2">{message}</p>
      <div className="mt-6 flex justify-center gap-2">
        {children}
        <Button asChild>
          <Link to="/">Back to Hearth</Link>
        </Button>
      </div>
    </div>
  );
}
